import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";

import { useTranslation } from "react-i18next";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "swiper/css";
import "swiper/css/pagination";
import debounce from 'lodash/debounce';
import ErrorBanner from "../components/ui/ErrorBanner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import FloatingMenu from "../components/FloatingMenu";
import BannerSlider from "../components/HomePage/BannerSlider";
import LanguageContext from "../context/LanguageContext";

import { RiMoreFill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";

import { Link, useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { useAuth } from "../context/AuthContext";
import { fetchGameList, fetchGameCategoryList, launchGame } from "../utils/api";
import { assetPath } from "../utils/assets";
import { requestTelegramReauth } from "../utils/authSession";

const HomePageContent = () => {
    const { t, i18n } = useTranslation();
    const { setLoading } = useLoading();
    const { token } = useAuth();
    const navigate = useNavigate();

    const { getLanguageId } = useContext(LanguageContext);

    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [gamesLoading, setGamesLoading] = useState(true);

    const [selectedGame, setSelectedGame] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [errorMessage, setErrorMessage] = useState("");

    const [gamesList, setGamesList] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);

    const [visibleGamesByCategory, setVisibleGamesByCategory] = useState({});
    const [categorySpecificLoading, setCategorySpecificLoading] = useState({});

    const [showModal, setShowModal] = useState(false);
    const [externalUrl, setExternalUrl] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [showModal]);

    const categoryIcons = useMemo(() => ({
        'SLOT': <img src={assetPath("/icons/slots.png")} alt="Slots" className="size-sm" />,
        'SPORTBOOK': <img src={assetPath("/icons/sports.png")} alt="Sports" className="size-sm" />,
        'LIVECASINO': <img src={assetPath("/icons/livecasino.png")} alt="Casino" className="size-sm" />,
        'COCKFIGHT': <img src={assetPath("/icons/cockfight.png")} alt="Cockfight" className="size-sm" />,
        'ARCADE': <img src={assetPath("/icons/arcade.png")} alt="Arcade" className="size-sm" />,
        'LOTTO': <img src={assetPath("/icons/lotto.png")} alt="Lotto" className="size-sm" />,
    }), []);

    const providerImageClass = "block mx-auto h-40 w-full object-cover object-center rounded-sm sm:h-44 lg:h-[280px] lg:rounded-t-[2rem]";

    const gameProviderImages = useMemo(() => ({
        "WM": {
            image: <img src={assetPath("/icons/wanmei_banner.png")} alt="Wan Mei" className={providerImageClass} />,
        },
        "PG": {
            image: <img src={assetPath("/icons/pragmaticplay_banner.png")} alt="Pragmatic" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(63, 14, 36, 0) 3.53%, #3F0E24 77.06%)",
        },
        "SB": {
            image: <img src={assetPath("/icons/sbo_banner.png")} alt="SBO" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(16, 100, 210, 0) 0%, #1064D2 86.47%)",
        },
        "top_games": {
            image: <img src={assetPath("/icons/evolution_banner.png")} alt="Evolution" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(69, 136, 57, 0) 0%, #458839 86.47%)",
        },
        "PGSOFT": {
            image: <img src={assetPath("/icons/pgsoft_banner.png")} alt="PG Soft" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(138, 102, 188, 0) 3.53%, #8A66BC 77.06%)",
        },
        "SEXYBCRT": {
            image: <img src={assetPath("/icons/sexybacarrat_banner.png")} alt="Sexy Baccarat" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(179, 29, 38, 0) 0%, #B31D26 86.47%)",
        },
        "NETENT": {
            image: <img src={assetPath("/icons/netent_banner.png")} alt="Netent" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(102, 131, 82, 0) 3.53%, #668352 88.24%)",
        },
        "REDTIGER": {
            image: <img src={assetPath("/icons/redtiger_banner.png")} alt="Red Tiger" className={providerImageClass} />,
            background: "linear-gradient(177.38deg, rgba(180, 0, 26, 0) 2.18%, #B4001A 97.94%)",
        },
        "BTG": {
            image: <img src={assetPath("/icons/bigtimegaming_banner.png")} alt="Big Time Gaming" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(16, 100, 210, 0) 0%, #1064D2 86.47%)",
        },
        "NLC": {
            image: <img src={assetPath("/icons/nolimitcity_banner.png")} alt="NoLimit City" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(46, 15, 60, 0) 3.53%, #2E0F3C 77.06%)",
        },
        "EXTNETENT": {
            image: <img src={assetPath("/icons/extend_netent_banner.png")} alt="Extended Netent" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(30, 69, 0, 0) 0%, #1E4500 67.06%)",
        },
        "SV388": {
            image: <img src={assetPath("/icons/svcockfight_banner.png")} alt="SV Cockfight" className={providerImageClass} />,
            background: "linear-gradient(177.38deg, rgba(14, 41, 121, 0) 2.18%, #0E2979 97.94%)",
        },
        "NETENTAC": {
            image: <img src={assetPath("/icons/netent_arcade_banner.png")} alt="Netent" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(6, 133, 67, 0) 0%, #068543 86.47%)",
        },
        "REDTIGERAC": {
            image: <img src={assetPath("/icons/redtiger_arcade_banner.png")} alt="Red Tiger" className={providerImageClass} />,
            background: "linear-gradient(177.38deg, rgba(180, 0, 26, 0) 2.18%, #B4001A 97.94%)",
        },
        "EEAI": {
            image: <img src={assetPath("/icons/eeai_banner.png")} alt="EEAI" className={providerImageClass} />,
        },
        "PT-LC": {
            image: <img src={assetPath("/icons/playtech_banner.png")} alt="Playtech" className={providerImageClass} />,
        },
        "PT-SL": {
            image: <img src={assetPath("/icons/playtechslot_banner.png")} alt="Playtech" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(16, 100, 210, 0) 0%, #1064D2 86.47%)",
        },
        "JL-SL": {
            image: <img src={assetPath("/icons/jili_banner.png")} alt="Playtech" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(16, 100, 210, 0) 0%, #bad761ff 86.47%)",
        },
        "JL-AC": {
            image: <img src={assetPath("/icons/netent_arcade_banner.png")} alt="Netent" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(6, 133, 67, 0) 0%, #068543 86.47%)",
        },
        "SP-SL": {
            image: <img src={assetPath("/icons/spribe_banner.png")} alt="Netent" className={providerImageClass} />,
            background: "linear-gradient(180deg, rgba(6, 133, 67, 0) 0%, #068543 86.47%)",
        },
    }), [providerImageClass]);

    const categoriesWithCounts = useMemo(() => {
        if (!Array.isArray(categoriesList) || !Array.isArray(gamesList)) return [];

        return categoriesList.map(category => {
            const gameCount = gamesList.filter(game => game.product_type_id === category.product_type_id).length;
            return {
                ...category,
                count: gameCount || 0
            };
        });
    }, [categoriesList, gamesList]);
    
    const categoriesToDisplay = useMemo(() => {
        if (!Array.isArray(categoriesWithCounts)) return [];

        let filteredCategories = categoriesWithCounts.filter(cat => cat.count > 0);

        if (selectedCategory && !selectedCategory.isLobby) {
            filteredCategories = categoriesWithCounts.filter((cat) => cat.category === selectedCategory.category);
        } else if (selectedGame) {
            filteredCategories = categoriesWithCounts.filter((cat) => cat.product_type_id === selectedGame.product_type_id);
        }

        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredCategories = filteredCategories.filter(cat =>
                cat.category.toLowerCase().includes(lowerSearchTerm)
            );
        }

        const customCategoryOrder = [
            "SLOT",
            "LIVECASINO",
            "SPORTBOOK",
            "COCKFIGHT",
            "ARCADE"
        ];

        return filteredCategories.sort((a, b) => {
            const aIndex = customCategoryOrder.indexOf(a.category);
            const bIndex = customCategoryOrder.indexOf(b.category);
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return a.category.localeCompare(b.category);
        });
    }, [selectedGame, selectedCategory, categoriesWithCounts, searchTerm]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setCategoriesLoading(true);
            setGamesLoading(true);
            setErrorMessage("");

            try {
                const [categoryResponse, gameResponse] = await Promise.all([
                    fetchGameCategoryList(),
                    fetchGameList({ category: "all" })
                ]);
                const sortedCategories = Array.isArray(categoryResponse.data)
                    ? [...categoryResponse.data]
                        .sort((a, b) => a.category.localeCompare(b.category))
                        .map(category => ({
                            ...category,
                            icon: categoryIcons[category.category]
                        }))
                    : [];
                
                const customOrder = [
                    "Pragmatic",
                    "PG Soft",
                    "Netent",
                    "Red Tiger",
                    "Big Time Gaming",
                    "NoLimit City",
                    "Extended Netent"
                ];

                const games = Array.isArray(gameResponse.data)
                ? gameResponse.data.map((game) => ({
                        ...game,
                        image: gameProviderImages[game.code]?.image,
                        color: gameProviderImages[game.code]?.background,
                    })).sort((a, b) => {
                        if (a.product_type_id === 3 && b.product_type_id === 3) {
                            const aIndex = customOrder.indexOf(a.product_name);
                            const bIndex = customOrder.indexOf(b.product_name);
                            if (aIndex !== -1 && bIndex !== -1) {
                                return aIndex - bIndex;
                            }
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            return a.product_name.localeCompare(b.product_name);
                        }
                        return a.product_type_id - b.product_type_id;
                    })
                : [];
                
                setCategoriesList(sortedCategories);
                setGamesList(games);

                const initialVisible = {};
                sortedCategories.forEach((cat) => {
                    initialVisible[cat.product_type_id] = 20;
                });
                setVisibleGamesByCategory(initialVisible);
            } catch (error) {
                setErrorMessage(t("errorLoadingData"));
            } finally {
                setLoading(false);
                setCategoriesLoading(false);
                setGamesLoading(false);
            }
        };

        const debouncedLoadData = debounce(() => {
            loadData();
        }, 1000, { leading: true, trailing: false });

        debouncedLoadData();

        return () => {
            debouncedLoadData.cancel();
        };
    }, [token, t, setLoading, i18n, getLanguageId, categoryIcons, gameProviderImages]);

    useEffect(() => {
        if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.ready();
            window.Telegram.WebApp.expand();
        }
    }, []);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    const handleShowMoreForCategory = useCallback((catId) => {
        setCategorySpecificLoading((prev) => ({ ...prev, [catId]: true }));
        setTimeout(() => {
            setVisibleGamesByCategory((prev) => ({
                ...prev,
                [catId]: (prev[catId] || 20) + 20,
            }));
            setCategorySpecificLoading((prev) => ({ ...prev, [catId]: false }));
        }, 1000);
    }, []);

    const handlePlayGame = useCallback(
        async (gameCode) => {
            if (!token) {
                if (requestTelegramReauth("play-game-without-token")) {
                    return;
                }

                navigate("/login");
                return;
            }

            setLoading(true);
            setModalLoading(true);
            setErrorMessage("");

            try {
                const response = await launchGame(gameCode);
                if (response.data) {
                    setExternalUrl(response.data);
                    setShowModal(true);
                } else {
                    alert(t("failedToLaunchGame"));
                }
            } catch (error) {
                setErrorMessage(t("errorLaunchingGame"));
            } finally {
                setModalLoading(false);

                setTimeout(() => {
                    setLoading(false);
                }, 1000);
            }
        },
        [navigate, t, token, setLoading]
    );

    const handleBack = useCallback(() => {
        setSelectedGame(null);
        setSelectedCategory(null);
        setSearchTerm('');
    }, []);

    return (
        <div className="main-container flex flex-col min-h-screen pb-[56px] lg:bg-transparent lg:pb-8">
            <div className="mx-auto flex w-full max-w-7xl flex-col lg:px-8 lg:pt-[162px] lg:pb-6">
                <div className="lg:hidden">
                    <Header />
                </div>

                <div className="lg:relative lg:left-1/2 lg:w-screen lg:-translate-x-1/2 lg:px-0 lg:pt-0">
                    <ErrorBanner message={errorMessage} />
                </div>

                <div className="lg:pt-0 lg:relative lg:left-1/2 lg:w-screen lg:-translate-x-1/2">
                    <div className="w-full overflow-hidden bg-[#07131d] lg:h-auto lg:overflow-visible">
                        <BannerSlider />
                    </div>
                </div>

            {(categoriesLoading || gamesLoading) ? (
                <div className="p-4 bg-primary lg:bg-transparent lg:px-0 lg:py-4">
                    <div className="desktop-surface">
                    <div className="mb-4 flex-between">
                        <Skeleton
                            width={120}
                            height={24}
                            baseColor="#1e1e2e"
                            highlightColor="#374151"
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-2 lg:grid-cols-4 2xl:grid-cols-5 lg:gap-6">
                        {[...Array(9)].map((_, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden lg:rounded-[2rem]"
                            >
                                <Skeleton
                                    height={144}
                                    width="100%"
                                    borderRadius="0.375rem"
                                    baseColor="#1e1e2e"
                                    highlightColor="#374151"
                                />
                                <div className="p-4">
                                    <Skeleton
                                        width="80%"
                                        height={12}
                                        baseColor="#1e1e2e"
                                        highlightColor="#374151"
                                    />
                                    <Skeleton
                                        width="60%"
                                        height={10}
                                        baseColor="#1e1e2e"
                                        highlightColor="#374151"
                                        className="mt-2"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            ) : (
                categoriesToDisplay.map((cat) => {
                    const customOrder = [
                        "Pragmatic",
                        "PG Soft",
                        "Netent",
                        "Red Tiger",
                        "Big Time Gaming",
                        "NoLimit City",
                        "Extended Netent"
                    ];
            
                    let gamesForCategory = gamesList.filter(
                        (game) => game.product_type_id === cat.product_type_id
                    );
            
                    gamesForCategory = gamesForCategory.sort((a, b) => {
                        const aIndex = customOrder.indexOf(a.product_name);
                        const bIndex = customOrder.indexOf(b.product_name);
                        if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex;
                        }
                        if (aIndex !== -1) return -1;
                        if (bIndex !== -1) return 1;
                        return a.product_name.localeCompare(b.product_name);
                    });
                    
                    const totalGamesInCategories = categoriesToDisplay.reduce(
                        (sum, cat) =>
                            sum + gamesList.filter((game) => game.product_type_id === cat.product_type_id).length,
                        0
                    );
                    const visibleCount = visibleGamesByCategory[cat.product_type_id] || 20;

                    return (
                        <div
                            key={cat.product_type_id}
                            className="p-4 bg-primary lg:bg-transparent lg:px-0 lg:py-4"
                        >
                            <div className="desktop-surface">
                            {totalGamesInCategories !== gamesList.length && (
                                <button
                                    className="btn-back flex gap-2 mb-6 lg:rounded-full lg:bg-[#04537F] lg:px-4 lg:py-2 lg:text-sm lg:font-semibold lg:text-color-blue lg:transition-colors lg:duration-300 lg:hover:bg-[#0270AE]"
                                    onClick={handleBack}
                                >
                                    <IoMdArrowBack className="size-sm text-color-blue lg:text-current" />
                                    <span className="text-sm text-color-blue lg:text-current">{t("back")}</span>
                                </button>
                            )}

                            <div className="mb-3 flex-between lg:mb-5 lg:gap-3">
                                <div className="flex-center gap-2 text-sm font-bold text-white lg:gap-3">
                                    <div className="contents lg:flex lg:h-12 lg:w-12 lg:items-center lg:justify-center lg:rounded-2xl lg:bg-[#04537F]">
                                    <span>
                                        {categoryIcons[cat.category] || (
                                            <img
                                                src={assetPath("/icons/history.png")}
                                                alt="Default"
                                                className="size-sm object-contain"
                                            />
                                        )}
                                    </span>
                                    </div>
                                <span className="font-black">
                                        {t(cat.category)}
                                    </span>
                                </div>
                            </div>

                            {gamesForCategory.length > 0 ? (
                                <div className="grid grid-cols-3 gap-2 lg:grid-cols-4 2xl:grid-cols-5 lg:gap-6 lg:justify-items-start">
                                    {gamesForCategory.slice(0, visibleCount).map((game) => (
                                        <div
                                            key={game.product_code}
                                            className={`group relative overflow-hidden lg:w-full lg:max-w-[220px] lg:rounded-[2rem] lg:border lg:border-[#0C77B1]/50 lg:bg-slate-950 lg:shadow-sm lg:transition-all lg:duration-500 lg:ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                                game.maintenance_status === 1 
                                                    ? 'cursor-not-allowed opacity-70' 
                                                    : 'cursor-pointer lg:hover:-translate-y-1 lg:hover:shadow-[0_24px_40px_-28px_rgba(15,23,42,0.9)] lg:active:scale-[0.985]'
                                            }`}
                                        >
                                            {game.image}
                                            {game.maintenance_status === 1 && (
                                                <div className="absolute inset-0 z-20 flex-center bg-black/50 lg:bg-slate-950/70 lg:backdrop-blur-sm">
                                                    <span className="text-warning-red font-bold text-sm mb-2 text-center">
                                                        {t("underMaintenance")}
                                                    </span>
                                                </div>
                                            )}

                                            {game.maintenance_status !== 1 && (
                                                <div className="absolute inset-0 z-10 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-[#034A73CC] lg:bg-[linear-gradient(180deg,rgba(2,112,174,0.02)_0%,rgba(2,112,174,0.84)_100%)] lg:transition-all lg:duration-500 lg:ease-[cubic-bezier(0.22,1,0.36,1)]">
                                                    <div className="relative z-20 h-full flex-col flex-center">
                                                        <button
                                                            className="btn-gradient-border container-sm rounded-lg text-xs font-bold text-black lg:rounded-full lg:font-black lg:shadow-[0_16px_30px_-24px_rgba(0,151,201,1)]"
                                                            onClick={() => handlePlayGame(game.code)}
                                                        >
                                                            {t("play")}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 flex flex-col justify-end transition-opacity duration-300 group-hover:opacity-0">
                                                <div 
                                                    className="text-center h-[85px] rounded-b-sm flex flex-col justify-end p-2 lg:h-[88px] lg:rounded-b-[2rem] lg:p-3"
                                                    style={{ background: game.color }}
                                                >
                                                    <div 
                                                        style={{
                                                            fontFamily: 'Impact',
                                                            fontWeight: 400,
                                                            fontSize: '17px',
                                                            lineHeight: '100%',
                                                            letterSpacing: '0%',
                                                            textAlign: 'center',
                                                            textTransform: 'uppercase'
                                                        }} 
                                                        className="text-white lg:text-[15px] lg:sm:text-[17px]"
                                                    >
                                                        {game.product_name === "SV Cockfight" ? "SV388" : game.product_name}
                                                    </div>
                                                    <p className="mt-2 text-xxxs text-white border-solid border-[1px] border-white rounded-full uppercase px-2 inline-block mx-auto lg:px-2 lg:py-1 lg:tracking-[0.22em] lg:text-white/90 lg:border-white/80">
                                                        {game.product_code === "PG" ? "PP" : game.product_code}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-6 text-center text-sm text-white lg:text-color-white-opacity">
                                    {t("noGamesAvailable")}
                                </div>
                            )}

                            {visibleCount < gamesForCategory.length && (
                                <div className="mt-3 flex-center lg:mt-5">
                                    {categorySpecificLoading[cat.product_type_id] ? (
                                        <div className="container-md btn-gradient-border gap-2 rounded-full animate-pulse">
                                            <span className="text-sm font-bold lg:font-black">
                                                {t("loading")}
                                            </span>
                                        </div>
                                    ) : (
                                        <button
                                            className="container-md btn-gradient-border gap-2 rounded-full lg:text-sm lg:font-black lg:shadow-[0_18px_36px_-24px_rgba(0,151,201,0.95)] lg:transition-transform lg:duration-300 lg:ease-[cubic-bezier(0.22,1,0.36,1)] lg:hover:-translate-y-0.5 lg:active:scale-[0.98]"
                                            onClick={() => handleShowMoreForCategory(cat.product_type_id)}
                                        >
                                            <RiMoreFill className="size-sm" />
                                            <span className="text-sm font-bold lg:font-black">
                                                {t("showMore")}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    );
                })
            )}

            {showModal && (
                <div className="modal-overlay fixed inset-0 bg-black/80 z-50 flex flex-col">
                    <div className="sticky top-0 z-60 flex-between bg-fourth px-4 py-5">
                        <div
                            className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                            onClick={() => {
                                setShowModal(false);
                                setModalLoading(false);
                                setExternalUrl(null);
                            }}
                        >
                            <IoMdArrowBack className="size-sm" />
                            <span className="text-sm">
                                {t("backToLobby")}
                            </span>
                        </div>
                        <button
                            className="cursor-pointer"
                            onClick={() => {
                                setShowModal(false);
                                setModalLoading(false);
                                setExternalUrl(null);
                            }}
                        >
                            <IoClose className="size-lg text-color-blue" />
                        </button>
                    </div>

                    {modalLoading ? (
                        <div className="p-4 rounded-lg">
                            <Skeleton
                                height={384}
                                borderRadius="0.75rem"
                                baseColor="#d1d5db"
                                highlightColor="#f3f4f6"
                            />
                        </div>
                    ) : (
                        <iframe
                            src={externalUrl}
                            title={t("gameLauncher")}
                            className="w-full h-full border-none"
                            style={{ minHeight: "80%", minWidth: "100%" }}
                            allowFullScreen
                        />
                    )}
                </div>
            )}

            <div className="lg:px-0 lg:pt-2">
                <div className="overflow-hidden desktop-surface">
                    <Footer />
                </div>
            </div>
            </div>
            
            {!showModal && <FloatingMenu />}
            {!showModal && <FloatingButtons />}
        </div>
    );
};

const HomePage = () => {
    return <HomePageContent />;
};

// === Export ===
export default HomePage;